import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Dashboard/Sidebar';
import { FilterComponent, UserPreferences } from '../components/Dashboard/FilterComponent';

interface CVData {
  filename: string;
  size: number;
  url: string;
  uploaded_at: string;
  content_type: string;
}

interface UserProfileData {
  name: string;
  years_of_experience: number | null;
}

export const CandidateSettingsPage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [currentCV, setCurrentCV] = useState<CVData | null>(null);
  const [loadingCV, setLoadingCV] = useState(true);
  const [uploadingCV, setUploadingCV] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<UserProfileData>({
    name: '',
    years_of_experience: null,
  });
  const [initialProfileData, setInitialProfileData] = useState<UserProfileData>({
    name: '',
    years_of_experience: null,
  });
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>({
    country: '',
    city: '',
    seniority: '',
    industry: '',
    role: '',
  });
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [preferencesError, setPreferencesError] = useState<string | null>(null);
  const [preferencesSuccess, setPreferencesSuccess] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Fetch current CV, preferences, and profile data on mount
  useEffect(() => {
    if (user) {
      fetchCurrentCV();
      fetchUserPreferences();
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCurrentCV = async () => {
    try {
      setLoadingCV(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/get-cv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: {
            sub: user?.id,
            email: user?.email,
          },
        }),
      });

      const data = await response.json();
      if (data.success && data.cv) {
        setCurrentCV(data.cv);
      }
    } catch (err) {
      console.error('Error fetching CV:', err);
    } finally {
      setLoadingCV(false);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      setLoadingPreferences(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/get-user-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: {
            sub: user?.id,
            email: user?.email,
          },
        }),
      });

      const data = await response.json();
      if (data.success && data.preferences) {
        setPreferences({
          country: data.preferences.country || '',
          city: data.preferences.city || '',
          seniority: data.preferences.seniority || '',
          industry: data.preferences.industry || '',
          role: data.preferences.role || '',
        });
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setPreferencesError('Failed to load preferences');
    } finally {
      setLoadingPreferences(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/get-user-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: {
            sub: user?.id,
            email: user?.email,
          },
        }),
      });

      const data = await response.json();
      if (data.success && data.profile) {
        const profile = {
          name: data.profile.name || '',
          years_of_experience: data.profile.years_of_experience || null,
        };
        setProfileData(profile);
        setInitialProfileData(profile); // Store initial data for comparison
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setProfileError('Failed to load profile data');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleUpdateProfile = async () => {
    setProfileError(null);
    setProfileSuccess(null);

    // Validate inputs
    if (!profileData.name.trim()) {
      setProfileError('Name is required');
      return;
    }

    if (profileData.years_of_experience !== null && profileData.years_of_experience < 0) {
      setProfileError('Years of experience must be a positive number');
      return;
    }

    try {
      setUpdatingProfile(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/update-user-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: {
            sub: user?.id,
            email: user?.email,
          },
          profile: {
            name: profileData.name,
            years_of_experience: profileData.years_of_experience,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      // Update initial data to reflect saved changes
      setInitialProfileData(profileData);
      setProfileSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setProfileSuccess(null);
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      setProfileError(errorMessage);
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Check if profile has changed
  const hasProfileChanged = () => {
    return (
      profileData.name !== initialProfileData.name ||
      profileData.years_of_experience !== initialProfileData.years_of_experience
    );
  };

  const handleUpdatePreferences = async (updatedPreferences: UserPreferences) => {
    setPreferencesError(null);
    setPreferencesSuccess(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/update-user-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: {
            sub: user?.id,
            email: user?.email,
          },
          preferences: updatedPreferences,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update preferences');
      }

      setPreferences(updatedPreferences);
      setPreferencesSuccess('Preferences updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setPreferencesSuccess(null);
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update preferences';
      setPreferencesError(errorMessage);
      throw err; // Re-throw so FilterComponent can handle it
    }
  };

  const validateFile = (file: File): string | null => {
    const SUPPORTED_TYPES = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
    ];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    if (!SUPPORTED_TYPES.includes(file.type)) {
      return 'Please upload a PDF, DOCX, DOC, or TXT file';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB';
    }
    return null;
  };

  const handleFileUpload = async (file: File) => {
    setError(null);
    setSuccess(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setUploadingCV(true);

      const formData = new FormData();
      formData.append('userData', JSON.stringify({
        sub: user?.id,
        email: user?.email,
      }));
      formData.append('cvFile', file);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/update-cv`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload CV');
      }

      setSuccess('CV updated successfully!');
      setCurrentCV(data.cv);

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload CV');
    } finally {
      setUploadingCV(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    setDeletingAccount(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: {
            sub: user?.id,
            email: user?.email,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete account');
      }

      // Account deleted successfully - logout and redirect to home
      await supabase.auth.signOut();
      navigate('/');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
      setDeletingAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <div className="main-content">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-10 animate-fade-in">
            <h1 className="heading-1 mb-2">Settings</h1>
            <p className="text-body text-base md:text-lg">
              Manage your profile, preferences, and account settings.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8 animate-fade-in animate-delay-100">
            {/* Personal Profile Section */}
            <section className="card-premium p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                  <h2 className="heading-2 mb-1">Personal Profile</h2>
                  <p className="text-xs md:text-sm text-slate-500">Update your personal information</p>
                </div>
              </div>

              {loadingProfile ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
                  <p className="text-sm font-medium text-slate-500">Loading profile...</p>
                </div>
              ) : (
                <div className="space-y-4 md:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-xs md:text-sm font-medium text-slate-700">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        placeholder="Enter your full name"
                        className="input-premium text-sm md:text-base"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="years_of_experience" className="block text-xs md:text-sm font-medium text-slate-700">
                        Years of Experience
                      </label>
                      <input
                        id="years_of_experience"
                        type="number"
                        min="0"
                        value={profileData.years_of_experience ?? ''}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          years_of_experience: e.target.value ? parseInt(e.target.value) : null
                        })}
                        placeholder="Enter years"
                        className="input-premium text-sm md:text-base"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleUpdateProfile}
                      disabled={updatingProfile || !hasProfileChanged()}
                      className={`btn-primary min-w-[140px] flex items-center justify-center gap-2 ${!hasProfileChanged() && !updatingProfile ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                      {updatingProfile ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Updating...</span>
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>

                  {/* Profile Success/Error Messages */}
                  {profileSuccess && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 animate-fade-in">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-green-700 font-medium">{profileSuccess}</p>
                    </div>
                  )}

                  {profileError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700 font-medium">{profileError}</p>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* CV Upload Section */}
            <section className="card-premium p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                  <h2 className="heading-2 mb-1">CV/Resume</h2>
                  <p className="text-xs md:text-sm text-slate-500">Manage your CV for better job matching</p>
                </div>
              </div>

              {loadingCV ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
                  <p className="text-sm font-medium text-slate-500">Loading CV...</p>
                </div>
              ) : uploadingCV ? (
                <div className="flex flex-col items-center justify-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-base font-semibold text-slate-700">Uploading...</p>
                  <p className="text-sm text-slate-500 mt-1">Please wait while we process your file</p>
                </div>
              ) : currentCV ? (
                <div className="space-y-4 md:space-y-6">
                  <div className="p-4 md:p-6 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center justify-between gap-3 md:gap-4 flex-wrap">
                      <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-xl border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                          <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm md:text-base font-semibold text-slate-900 truncate">{currentCV.filename}</p>
                          <p className="text-xs md:text-sm text-slate-500 mt-0.5">
                            {formatFileSize(currentCV.size)} • Uploaded {formatDate(currentCV.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 w-full sm:w-auto">
                        <button
                          onClick={handleClick}
                          disabled={uploadingCV}
                          className="btn-secondary text-xs md:text-sm py-2 flex-1 sm:flex-none"
                        >
                          Replace
                        </button>
                        <a
                          href={currentCV.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary text-xs md:text-sm py-2 flex-1 sm:flex-none text-center"
                        >
                          View
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 md:p-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all cursor-pointer group" onClick={handleClick}>
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-sm group-hover:scale-110 transition-transform duration-200">
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2">Upload your CV</h3>
                  <p className="text-xs md:text-sm text-slate-500 mb-4 md:mb-6 max-w-md mx-auto text-center px-4">
                    Drag and drop your file here, or click to browse. Supported formats: PDF, DOCX, DOC, TXT (Max 10MB)
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleClick(); }}
                    disabled={uploadingCV}
                    className="btn-primary text-sm md:text-base"
                  >
                    Select File
                  </button>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileInputChange}
                className="hidden"
                disabled={uploadingCV}
              />

              {error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              )}

              {success && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 animate-fade-in">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-green-700 font-medium">{success}</p>
                </div>
              )}
            </section>

            {/* User Preferences Section */}
            <section className="card-premium p-6 md:p-8">
              <div className="mb-4 md:mb-6">
                <h2 className="heading-2 mb-1">Job Preferences</h2>
                <p className="text-xs md:text-sm text-slate-500">Customize your job matching criteria</p>
              </div>

              {loadingPreferences ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
                  <p className="text-sm font-medium text-slate-500">Loading preferences...</p>
                </div>
              ) : (
                <>
                  <FilterComponent
                    initialPreferences={preferences}
                    onUpdate={handleUpdatePreferences}
                  />

                  {/* Success/Error Messages */}
                  {preferencesSuccess && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 animate-fade-in">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-green-700 font-medium">{preferencesSuccess}</p>
                    </div>
                  )}

                  {preferencesError && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700 font-medium">{preferencesError}</p>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Account Deletion Section */}
            <section className="card-premium p-6 md:p-8 border-red-100">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4 md:gap-6">
                <div className="flex-1">
                  <h2 className="text-lg md:text-xl font-bold text-red-600 mb-2">Delete Account</h2>
                  <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                </div>

                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 md:px-6 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-sm whitespace-nowrap flex-shrink-0 w-full sm:w-auto"
                >
                  Delete My Account
                </button>
              </div>

              {deleteError && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-700 font-medium">{deleteError}</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 shadow-2xl transform transition-all scale-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">
                  Delete Account
                </h3>
                <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                  Are you sure you want to delete your account? This will permanently delete:
                </p>
                <ul className="mt-3 md:mt-4 space-y-1.5 md:space-y-2 text-xs md:text-sm text-slate-600 list-disc list-inside bg-red-50 p-3 md:p-4 rounded-lg border border-red-100">
                  <li>Your profile and account information</li>
                  <li>Your uploaded CV/resume</li>
                  <li>Your job preferences and settings</li>
                  <li>All saved job matches and history</li>
                </ul>
                <p className="mt-3 md:mt-4 text-xs md:text-sm font-semibold text-red-600">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError(null);
                }}
                disabled={deletingAccount}
                className="flex-1 px-4 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  handleDeleteAccount();
                }}
                disabled={deletingAccount}
                className="flex-1 px-4 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deletingAccount ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
